/**
 * Utilidades para conversión bidireccional de slugs
 * Maneja la conversión entre nombres legibles y slugs URL-friendly
 */

// Mapeo de caracteres especiales para slugs
const SPECIAL_CHARS_MAP = {
    'á': 'a', 'à': 'a', 'ä': 'a', 'â': 'a', 'ā': 'a', 'ã': 'a',
    'é': 'e', 'è': 'e', 'ë': 'e', 'ê': 'e', 'ē': 'e',
    'í': 'i', 'ì': 'i', 'ï': 'i', 'î': 'i', 'ī': 'i',
    'ó': 'o', 'ò': 'o', 'ö': 'o', 'ô': 'o', 'ō': 'o', 'õ': 'o',
    'ú': 'u', 'ù': 'u', 'ü': 'u', 'û': 'u', 'ū': 'u',
    'ñ': 'n', 'ç': 'c',
    '&': 'y', '+': 'plus', '@': 'at'
};

// Mapeo inverso para recuperar caracteres originales (casos comunes)
const REVERSE_CHARS_MAP = {
    'n': ['ñ', 'n'],
    'c': ['ç', 'c'],
    'y': ['&', 'y'],
    'plus': ['+'],
    'at': ['@']
};

/**
 * Convierte un texto a slug URL-friendly
 * @param {string} text - Texto a convertir
 * @returns {string} Slug generado
 */
export const textToSlug = (text) => {
    if (!text || typeof text !== 'string') return '';
    
    return text
        .toLowerCase()
        .trim()
        // Reemplazar caracteres especiales
        .replace(/[áàäâāã]/g, 'a')
        .replace(/[éèëêē]/g, 'e')
        .replace(/[íìïîī]/g, 'i')
        .replace(/[óòöôōõ]/g, 'o')
        .replace(/[úùüûū]/g, 'u')
        .replace(/ñ/g, 'n')
        .replace(/ç/g, 'c')
        .replace(/&/g, 'y')
        .replace(/\+/g, 'plus')
        .replace(/@/g, 'at')
        // Reemplazar espacios y caracteres no alfanuméricos con guiones
        .replace(/[^a-z0-9]+/g, '-')
        // Eliminar guiones al inicio y final
        .replace(/^-+|-+$/g, '')
        // Eliminar guiones duplicados
        .replace(/-+/g, '-');
};

/**
 * Intenta recuperar el texto original desde un slug
 * @param {string} slug - Slug a convertir
 * @param {Array} referenceList - Lista de referencia para matching exacto
 * @returns {string} Texto recuperado o slug formateado
 */
export const slugToText = (slug, referenceList = []) => {
    if (!slug || typeof slug !== 'string') return '';
    
    // Si tenemos una lista de referencia, buscar coincidencia exacta
    if (referenceList && referenceList.length > 0) {
        const exactMatch = referenceList.find(item => {
            const itemSlug = textToSlug(item.nombre || item.name || item);
            return itemSlug === slug;
        });
        
        if (exactMatch) {
            return exactMatch.nombre || exactMatch.name || exactMatch;
        }
    }
    
    // Conversión básica: reemplazar guiones con espacios y capitalizar
    return slug
        .replace(/-/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
        .replace(/\bY\b/g, '&')
        .replace(/\bPlus\b/g, '+')
        .replace(/\bAt\b/g, '@');
};

/**
 * Genera un slug único agregando un sufijo numérico si es necesario
 * @param {string} text - Texto base
 * @param {Array} existingSlugs - Lista de slugs existentes
 * @returns {string} Slug único
 */
export const generateUniqueSlug = (text, existingSlugs = []) => {
    const baseSlug = textToSlug(text);
    
    if (!existingSlugs.includes(baseSlug)) {
        return baseSlug;
    }
    
    let counter = 1;
    let uniqueSlug = `${baseSlug}-${counter}`;
    
    while (existingSlugs.includes(uniqueSlug)) {
        counter++;
        uniqueSlug = `${baseSlug}-${counter}`;
    }
    
    return uniqueSlug;
};

/**
 * Valida si un slug es válido
 * @param {string} slug - Slug a validar
 * @returns {boolean} True si es válido
 */
export const isValidSlug = (slug) => {
    if (!slug || typeof slug !== 'string') return false;
    
    // Un slug válido solo contiene letras minúsculas, números y guiones
    // No puede empezar o terminar con guión
    const slugRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;
    return slugRegex.test(slug);
};

/**
 * Limpia y normaliza un slug
 * @param {string} slug - Slug a limpiar
 * @returns {string} Slug limpio
 */
export const cleanSlug = (slug) => {
    if (!slug || typeof slug !== 'string') return '';
    
    return slug
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '')
        .replace(/^-+|-+$/g, '')
        .replace(/-+/g, '-');
};

/**
 * Convierte un objeto de filtros a slugs
 * @param {Object} filters - Objeto con filtros
 * @param {Object} referenceData - Datos de referencia para conversión exacta
 * @returns {Object} Objeto con slugs
 */
export const filtersToSlugs = (filters, referenceData = {}) => {
    const slugs = {};
    
    Object.keys(filters).forEach(key => {
        const value = filters[key];
        if (value && typeof value === 'string') {
            const referenceList = referenceData[key] || [];
            slugs[key] = textToSlug(value);
        } else if (value) {
            slugs[key] = value;
        }
    });
    
    return slugs;
};

/**
 * Convierte slugs de vuelta a filtros legibles
 * @param {Object} slugs - Objeto con slugs
 * @param {Object} referenceData - Datos de referencia para conversión exacta
 * @returns {Object} Objeto con valores legibles
 */
export const slugsToFilters = (slugs, referenceData = {}) => {
    const filters = {};
    
    Object.keys(slugs).forEach(key => {
        const value = slugs[key];
        if (value && typeof value === 'string') {
            const referenceList = referenceData[key] || [];
            filters[key] = slugToText(value, referenceList);
        } else if (value) {
            filters[key] = value;
        }
    });
    
    return filters;
};

export default {
    textToSlug,
    slugToText,
    generateUniqueSlug,
    isValidSlug,
    cleanSlug,
    filtersToSlugs,
    slugsToFilters
};